import { ChevronDown } from '@dreamstack/icons/src/icon-components/ChevronDown'
import { ChevronUp } from '@dreamstack/icons/src/icon-components/ChevronUp'
import { Minus } from '@dreamstack/icons/src/icon-components/Minus'
import { Plus } from '@dreamstack/icons/src/icon-components/Plus'
import { cn } from '@dreamstack/util/src/cn'
import type { ReactElement, SVGProps } from 'react'
import { GridWithGaps } from '../layout/MainGrid'
import { HeadingH2 } from '../layout/Texts'

export type WidgetType =
  | 'DefaultAccordion'
  | 'ExpandableBlock'
  | 'ArrowAccordion'

// Generic factory to recreate previous styled icon behavior with Tailwind classes
const StyledIconFactory = (Icon: (props: SVGProps<any>) => ReactElement) => {
  const C = ({
    $smallSize,
    className,
    ...rest
  }: { $smallSize?: boolean } & SVGProps<any>) => (
    <Icon
      className={cn(
        'col-start-1 h-px-32 w-px-32 md:col-start-1 md:justify-self-start lg:col-start-1 lg:justify-self-end',
        !$smallSize && 'ml:h-px-64 ml:w-px-64',
        className
      )}
      {...rest}
    />
  )
  C.displayName = 'AccordionIcon'
  return C
}
export const StyledPlus = StyledIconFactory(Plus)
export const StyledMinus = StyledIconFactory(Minus)
export const StyledChevronUp = StyledIconFactory(ChevronUp)
export const StyledChevronDown = StyledIconFactory(ChevronDown)

export const StyledPanelContent = ({
  smallSize,
  open,
  widgetType = 'DefaultAccordion',
  noMarginTop,
  className,
  children,
  ...rest
}: React.PropsWithChildren<{
  smallSize?: boolean
  open: boolean
  widgetType?: WidgetType
  noMarginTop?: boolean
  className?: string
}>) => {
  return (
    <div
      className={cn(
        'text-base text-accentroGray-full mt-px-24 md:mt-px-32 lg:mt-px-40',
        !open && 'hidden',
        smallSize ? 'col-start-2 col-span-10' : 'col-start-3 col-span-10',
        widgetType === 'ArrowAccordion' && 'col-start-1',
        noMarginTop && 'mt-0 md:mt-0 lg:mt-0',
        className
      )}
      {...rest}
    >
      {children}
    </div>
  )
}
export const StyledGridWithGaps = ({
  useAqua,
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ useAqua?: boolean; className?: string }>) => (
  <GridWithGaps
    className={cn(
      'items-center',
      useAqua && 'text-accentroAqua-full',
      className
    )}
    {...rest}
  >
    {children}
  </GridWithGaps>
)

export const StyledHeadingH2 = ({
  smallSize,
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ smallSize?: boolean; className?: string }>) => (
  <HeadingH2
    className={cn(
      'font-semibold col-span-full',
      smallSize ? 'col-start-2 text-px-20' : 'col-start-3',
      className
    )}
    {...rest}
  >
    {children}
  </HeadingH2>
)

export const StyledDiv = ({
  useAqua,
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ useAqua?: boolean; className?: string }>) => (
  <div
    className={cn(
      'flex items-center',
      useAqua && 'text-accentroAqua-full',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

export const PaddingContainer = ({
  smallSize,
  className,
  children,
  ...rest
}: React.PropsWithChildren<{ smallSize?: boolean; className?: string }>) => (
  <div
    className={cn(
      smallSize ? 'py-px-24 md:py-px-32' : 'py-px-24 ml:py-px-40 lg:py-px-64',
      className
    )}
    {...rest}
  >
    {children}
  </div>
)

export const expandableTitle = (
  title: string,
  titleWhenExpanded: string | undefined,
  open: boolean
) => {
  if (open) {
    return titleWhenExpanded ? titleWhenExpanded : title
  } else {
    return title
  }
}
